/**
 * Shared hook for My Tasks data
 * Used by both MyTasksWidget and MyTasksTab to ensure consistent data
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskWithAssignments } from '@scrambl/shared';
import { tasksApi } from '@/lib/api/tasks.api';
// Store-based auth (not the AuthProvider context): this hook also runs in
// the Benny companion window, which has no AuthProvider. The store mirrors
// the context user in the main window (synced by MainLayout).
import { useAuth } from '@/store/auth.store';
import { useOrganizationStore } from '@/store/organization.store';
const MY_TASKS_FETCH_LIMIT = 500;

export interface UseMyTasksDataOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  includeCompleted?: boolean;
}

export interface UseMyTasksDataResult {
  tasks: TaskWithAssignments[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  invalidate: () => Promise<void>;
}

// Stable query key for my tasks - shared across all consumers
// Include a version segment to prevent collisions with older cache shapes.
export const MY_TASKS_QUERY_KEY = ['my-tasks', 'v2'] as const;

export function useMyTasksData(options: UseMyTasksDataOptions = {}): UseMyTasksDataResult {
  const {
    enabled = true,
    staleTime = 30000,
    gcTime = 10 * 60 * 1000,
    includeCompleted = false,
  } = options;

  const { user } = useAuth();
  const currentUserId = user?.id;
  const organizationId = useOrganizationStore((state) => state.currentOrganization?.id);
  const queryClient = useQueryClient();

  // Query key includes user ID to ensure user-specific caching
  const queryKey = [...MY_TASKS_QUERY_KEY, currentUserId, includeCompleted] as const;

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!currentUserId) {
        return { tasks: [], totalCount: 0 };
      }

      const params: Record<string, unknown> = {
        page: 1,
        limit: MY_TASKS_FETCH_LIMIT,
        // Keep newest updates in the first page so recently created tasks
        // are not pushed out when users have >limit historical tasks.
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        assigneeIds: [currentUserId],
        excludeCompleted: !includeCompleted,
        includePersonalTasks: true,
        includeSubtasks: true, // Include subtasks assigned to the user
        view: 'list',
      };

      const response = await tasksApi.getTasks(params, { signal });

      return {
        tasks: response.data || [],
        totalCount: response.meta?.totalItems || 0,
      };
    },
    enabled: enabled && !!currentUserId && !!organizationId,
    staleTime,
    gcTime,
    // Freshness comes from realtime cache updates + mutation writes; the global
    // refetch-on-focus (when stale) covers missed socket events without
    // refetching on every mount.
    refetchOnReconnect: true,
  });

  const refresh = async () => {
    await refetch();
  };

  const invalidate = async () => {
    // Invalidate all my-tasks queries (with any includeCompleted value)
    await queryClient.invalidateQueries({ queryKey: MY_TASKS_QUERY_KEY });
  };

  return {
    tasks: data?.tasks || [],
    isLoading,
    isFetching,
    error: error as Error | null,
    refresh,
    invalidate,
  };
}

// Helper to invalidate my tasks cache from anywhere
export function invalidateMyTasksCache(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: MY_TASKS_QUERY_KEY });
}

// Helper to update a task in the my tasks cache
export function updateTaskInMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  taskId: string,
  updater: (task: TaskWithAssignments) => TaskWithAssignments
) {
  queryClient.setQueriesData(
    { queryKey: MY_TASKS_QUERY_KEY },
    (oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined) => {
      if (!oldData?.tasks) return oldData;
      return {
        ...oldData,
        tasks: oldData.tasks.map(task =>
          task.id === taskId ? updater(task) : task
        ),
      };
    }
  );
}

// Helper to add a task to the my tasks cache
export function addTaskToMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  task: TaskWithAssignments
) {
  queryClient.setQueriesData(
    { queryKey: MY_TASKS_QUERY_KEY },
    (oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined) => {
      if (!oldData) return { tasks: [task], totalCount: 1 };
      return {
        ...oldData,
        tasks: [...oldData.tasks, task],
        totalCount: oldData.totalCount + 1,
      };
    }
  );
}

// Helper to remove a task from the my tasks cache
export function removeTaskFromMyTasksCache(
  queryClient: ReturnType<typeof useQueryClient>,
  taskId: string
) {
  queryClient.setQueriesData(
    { queryKey: MY_TASKS_QUERY_KEY },
    (oldData: { tasks: TaskWithAssignments[]; totalCount: number } | undefined) => {
      if (!oldData?.tasks) return oldData;
      return {
        ...oldData,
        tasks: oldData.tasks.filter(task => task.id !== taskId),
        totalCount: Math.max(0, oldData.totalCount - 1),
      };
    }
  );
}
